import React from "react";
import { ProgressBar } from "react-bootstrap";
import { useAuth } from "../Auth/useAuth";
import UiButton, { UI_BUTTON_TYPE } from "../ui/UiButton";
import UiStarBadge from "../ui/UiStarBadge";
import "./loyaltyProgress.css";

function LoyaltyProgress({
  spendingAmount,
  minThreshold,
  maxThreshold,
  amountThresholds,
  toggleDialog,
}) {
  const [auth] = useAuth();
  const lang = "fr";
  const userLoyaltyReward = auth?.user?.data.loyalty_reward;

  const OfferTextBlock = ({ title, desc }) => (
    <React.Fragment>
      <div className="spending-progress__header--text">{title}</div>
      {desc && <p className="spending-progress__status-desc">{desc}</p>}
    </React.Fragment>
  );

  const OfferStatus = ({ text, isBonus }) => (
    <React.Fragment>
      <div
        className={
          isBonus
            ? "spending-progress__status-offer"
            : "spending-progress__status-text"
        }
      >
        {text}
      </div>
    </React.Fragment>
  );

  const OfferDetails = ({ title, description }) => (
    <React.Fragment>
      <div className="spending-progress__status-title">{title}</div>
      <p className="spending-progress__status-desc">{description}</p>
    </React.Fragment>
  );

  const renderOfferDetails = () => {
    if (spendingAmount <= 0) {
      return (
        <React.Fragment>
          <OfferTextBlock
            title="Bienvenue chez Blancrème !"
            desc="Dès votre première commande, votre chiffre d'affaires cumulé se transforme en avantages concrets. Commandez maintenant et progressez vers le palier 1."
          />
        </React.Fragment>
      );
    }

    const firstThreshold = amountThresholds[0];
    const lastThreshold = amountThresholds[amountThresholds.length - 1];

    if (spendingAmount < firstThreshold) {
      return (
        <React.Fragment>
          <OfferTextBlock
            title="Félicitations, vous êtes en route vers le palier 1 !"
            desc="Commandez à nouveau pour recevoir votre premier avantage exclusif."
          />
          <OfferStatus text="BONUS PALIER 1" />
          <OfferDetails
            title={maxThreshold.text}
            description={maxThreshold.description}
          />
        </React.Fragment>
      );
    }

    if (spendingAmount < lastThreshold) {
      return (
        <React.Fragment>
          <OfferStatus text="VOTRE BONUS OFFERT" isBonus />
          <OfferDetails
            title={minThreshold.text}
            description={minThreshold.description}
          />
          <OfferStatus text="PROCHAIN PALIER" />
          <OfferDetails
            title={maxThreshold.text}
            description={maxThreshold.description}
          />
        </React.Fragment>
      );
    }

    // Max tier reached
    return (
      <React.Fragment>
        <OfferTextBlock
          title="Merci pour votre confiance !"
          desc="Vous avez atteint le palier 5. Nous sommes heureux de vous offrir :"
        />
        <OfferStatus text="VOTRE BONUS OFFERT" isBonus />
        <OfferDetails
          title={minThreshold.text}
          description={minThreshold.description}
        />
      </React.Fragment>
    );
  };

  const calculatePercentage = (current, min, max) => {
    const firstThreshold = amountThresholds?.[0] || 0;
    const lastThreshold = amountThresholds?.[amountThresholds.length - 1] || 0;

    if (current <= 0) return 0;
    if (current >= lastThreshold) return 100;

    if (current < firstThreshold) {
      return Math.round((current / firstThreshold) * 100);
    }

    const minAmt = min?.amount || 0;
    const maxAmt = max?.amount || firstThreshold;

    if (maxAmt - minAmt === 0) return 100;

    return Math.round(((current - minAmt) / (maxAmt - minAmt)) * 100);
  };

  const progressPercentage = calculatePercentage(
    spendingAmount,
    minThreshold,
    maxThreshold,
  );

  const firstThresholdAmt = amountThresholds?.[0] || 500;
  const lastThresholdAmt =
    amountThresholds?.[amountThresholds.length - 1] || 2500;

  const isFirstTier = spendingAmount < firstThresholdAmt;
  const isMaxTier = spendingAmount >= lastThresholdAmt;

  return (
    <React.Fragment>
      <div className="spending-progress">
        <div className="spending-progress__content">
          <div className="spending-progress__top">
            <UiStarBadge
              content={isFirstTier ? "1" : minThreshold?.rating || "1"}
              confirmationIcon={!isFirstTier}
              disabled={isFirstTier}
            />
            <span className="spending-progress__value">
              {spendingAmount?.toLocaleString(lang)} €
            </span>
          </div>

          <div>
            <ProgressBar
              className="spending-progress__bar"
              now={progressPercentage}
            />

            {!isMaxTier && (
              <div>
                <div className="spending-progress__steps">
                  <span>{!isFirstTier ? minThreshold?.rating || "" : ""}</span>
                  <span>{maxThreshold?.rating || ""}</span>
                </div>

                <div className="spending-progress__steps">
                  <span>
                    {!isFirstTier && minThreshold?.amount
                      ? minThreshold.amount
                      : ""}
                  </span>
                  <span>
                    {maxThreshold?.amount ? maxThreshold.amount : ""} €
                  </span>
                </div>
              </div>
            )}
          </div>

          <UiButton
            size="lg"
            classNames={["spending-progress__button"]}
            buttonUiType={[UI_BUTTON_TYPE.ONLY_BORDER]}
            content="Plus d’infos"
            onClick={toggleDialog}
          />
        </div>

        {userLoyaltyReward ? (
          <div className="spending-progress__status">
            {renderOfferDetails()}
          </div>
        ) : (
          ""
        )}
      </div>
    </React.Fragment>
  );
}

export default LoyaltyProgress;
